import { isNil } from 'lodash'

import { ALERT_FRAGMENT } from 'features/alerts/api/fragments/alert'
import { alertsFromDTO } from 'features/alerts/api/mappers/alertFromDTO'
import { DomainAlert } from 'features/inventory/domain/types/DomainAlert'
import { Domain } from 'features/inventory/types/domain'
import { WorkspaceDomainDTO, WorkspaceDTO } from 'features/workspace/api/types/workspaceDTO'
import { Workspace } from 'features/workspace/types/workspace'
import { WorkspaceAlert } from 'features/workspace/types/workspaceAlert'
import { DEFAULT_REFETCH_INTERVAL } from 'providers/tanstack-query/constants'
import { useQuery } from 'providers/tanstack-query/useQuery'
import { gql } from 'providers/tanstack-query/utils'
import { sortToDTO } from '../api/mapper'
import { UseGetWorkspacesProps, Variables } from '../api/types'

export type WorkspaceDomainAlerts = Pick<Workspace, 'alerts' | 'id'> & {
  domains: DomainAlerts[]
}

type Data = {
  workspaces: {
    nodes:
      | (Pick<WorkspaceDTO, 'alerts' | 'id'> & {
          domains: Pick<WorkspaceDomainDTO, 'alerts' | 'id'>[]
        })[]
      | null
  }
}
type DomainAlerts = Pick<Domain, 'alerts' | 'id'>

export const useGetFreshWorkspaceDomainAlerts = ({
  filters,
  first,
  last,
  sort,
}: UseGetWorkspacesProps): WorkspaceDomainAlerts[] => {
  const { data } = useQuery<Variables, Data, WorkspaceDomainAlerts[]>({
    options: {
      refetchInterval: DEFAULT_REFETCH_INTERVAL,
      select: ({ workspaces: { nodes } }: Data): WorkspaceDomainAlerts[] => {
        if (isNil(nodes)) {
          return []
        }

        return nodes.map(
          ({ alerts, domains, id }): WorkspaceDomainAlerts => ({
            alerts: alertsFromDTO<WorkspaceAlert>(alerts),
            domains: domains.map(
              ({ alerts, id }): DomainAlerts => ({
                alerts: alertsFromDTO<DomainAlert>(alerts),
                id,
              }),
            ),
            id,
          }),
        )
      },
    },
    query: gql`
      ${ALERT_FRAGMENT}

      query getFreshWorkspaceAlerts(
        $first: Int
        $last: Int
        $terms: [String!]
        $sort: WorkspaceSortInput
      ) {
        workspaces(first: $first, last: $last, terms: $terms, sort: $sort) {
          nodes {
            id
            alerts {
              ...AlertFragment
            }
            domains {
              id
              alerts {
                ...AlertFragment
              }
            }
          }
        }
      }
    `,
    variables: {
      first,
      last,
      sort: sortToDTO(sort),
      terms: filters,
    },
  })

  return data ?? []
}
